import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import TourCard from "@/components/site/TourCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ToursPage() {
  const { tours, destinations, search, setSearch, t } = useSite();
  const [params, setParams] = useSearchParams();
  const dest = params.get("destino") || "all";
  const [sort, setSort] = React.useState("order");

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    let l = tours.filter((x) => (dest === "all" || (x.locations || []).includes(dest)) &&
      (!q || x.title.toLowerCase().includes(q) || (x.description || "").toLowerCase().includes(q)));
    if (sort === "price_asc") l = [...l].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") l = [...l].sort((a, b) => b.price - a.price);
    if (sort === "offer") l = [...l].sort((a, b) => Number(b.offer) - Number(a.offer));
    return l;
  }, [tours, dest, search, sort]);

  return (
    <div className="bg-gray-50 min-h-[70vh]" data-testid="tours-page">
      <div className="brand-bg">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-14 text-white">
          <h1 className="text-3xl sm:text-5xl font-bold">{t("tours")}</h1>
          <p className="mt-3 text-white/90">{dest === "all" ? "Todas nuestras experiencias en República Dominicana" : dest}</p>
        </div>
      </div>
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="destination-filters">
          {[{ name: "all", label: t("all") }, ...destinations.map((d) => ({ name: d.name, label: d.name }))].map((d) => (
            <button key={d.name} onClick={() => setParams(d.name === "all" ? {} : { destino: d.name })} data-testid={`filter-${d.name}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${dest === d.name ? "brand-bg text-white border-transparent" : "bg-white text-gray-700 border-gray-200 hover:border-[var(--brand)]"}`}>
              {d.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} data-testid="tours-search-input"
              className="w-full h-11 rounded-md border border-gray-200 bg-white pl-10 pr-4 text-sm outline-none focus:border-[var(--brand)]" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-56 h-11 bg-white" data-testid="tours-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="order">Recomendados</SelectItem>
              <SelectItem value="offer">Ofertas primero</SelectItem>
              <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
              <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {list.length === 0 ? (
          <p className="text-center text-gray-500 py-20" data-testid="tours-empty">{t("noResults")}</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {list.map((tour) => <TourCard key={tour.id} tour={tour} />)}
          </div>
        )}
      </div>
    </div>
  );
}
